import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

import { AuthProvider, useAuth } from "@/lib/auth";
import { I18nProvider } from "@/lib/i18n";

import LoginPage from "@/pages/Login";
import ForgotPasswordPage from "@/pages/ForgotPassword";
import ResetPasswordPage from "@/pages/ResetPassword";
import KioskPage from "@/pages/Kiosk";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Monitor from "@/pages/Monitor";
import MyStatus from "@/pages/MyStatus";
import Leave from "@/pages/Leave";
import Approvals from "@/pages/Approvals";
import Employees from "@/pages/Employees";
import Reports from "@/pages/Reports";
import MonthlyCard from "@/pages/MonthlyCard";
import Anomalies from "@/pages/Anomalies";
import Audit from "@/pages/Audit";
import Settings from "@/pages/Settings";

function Protected({ children, roles }) {
  const { user } = useAuth();
  if (user === undefined)
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="font-display text-2xl animate-pulse">Loading…</div>
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/app/dashboard" replace />;
  return children;
}

function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/kiosk" element={<KioskPage />} />
            <Route
              path="/app"
              element={
                <Protected>
                  <Layout />
                </Protected>
              }
            >
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="monitor" element={<Monitor />} />
              <Route path="my-status" element={<MyStatus />} />
              <Route path="leave" element={<Leave />} />
              <Route
                path="approvals"
                element={
                  <Protected roles={["manager", "hr", "admin"]}>
                    <Approvals />
                  </Protected>
                }
              />
              <Route
                path="employees"
                element={
                  <Protected roles={["hr", "admin"]}>
                    <Employees />
                  </Protected>
                }
              />
              <Route
                path="reports"
                element={
                  <Protected roles={["manager", "hr", "admin"]}>
                    <Reports />
                  </Protected>
                }
              />
              <Route
                path="monthly-card"
                element={
                  <Protected roles={["manager", "hr", "admin"]}>
                    <MonthlyCard />
                  </Protected>
                }
              />
              <Route
                path="anomalies"
                element={
                  <Protected roles={["manager", "hr", "admin"]}>
                    <Anomalies />
                  </Protected>
                }
              />
              <Route
                path="audit"
                element={
                  <Protected roles={["hr", "admin"]}>
                    <Audit />
                  </Protected>
                }
              />
              <Route path="settings" element={<Settings />} />
            </Route>
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
          <Toaster position="top-right" />
        </BrowserRouter>
      </AuthProvider>
    </I18nProvider>
  );
}

export default App;
